import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  MenuOpen,
  Menu,
  FullscreenExit,
  Fullscreen,
  ExploreOutlined,
  VerticalSplitOutlined,
  WebAssetOutlined,
  SwapHorizontalCircle,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";


import preimageLogo from "assets/images/preimagePointCloudViewer.svg";
import { usePtcldScriptContext } from "contexts/PtcldScriptContext";

interface PointCloudViewProps {
  potreeContainerDiv: React.RefObject<HTMLDivElement>;
  viewerRef: React.RefObject<Potree.Viewer | null>;
  scaleFactor?: number;
}

const PointCloudView = ({ potreeContainerDiv, viewerRef, scaleFactor }: PointCloudViewProps) => {
  const {
    pointcloudLoading,
    ifcLoading,
    scriptsLoaded,
    loadingProgression,
    setLoadingProgression,
    setPointcloudLoading,
    isSplit,
    setIsSplit,
    width,
    menu,
    setMenuOpen
  } = usePtcldScriptContext();

  const parentRef = useRef<HTMLDivElement>(null);
  const anyoneLoading = pointcloudLoading || ifcLoading || !scriptsLoaded;
  const allLoading = !pointcloudLoading && !ifcLoading && scriptsLoaded;

  useEffect(() => {
    if (viewerRef.current && scaleFactor) {
      viewerRef.current.setExternalMeasureScale(scaleFactor);
    }

    return () => {
      if (viewerRef.current) {
        viewerRef.current.setExternalMeasureScale(1);
      }
    };
  }, [viewerRef, scaleFactor]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (pointcloudLoading) {
      interval = setInterval(() => {
        const randomInc = Math.ceil(Math.random() * 10);
        setLoadingProgression((prevProgress: number) => {
          if (prevProgress >= 95) {
            if (interval !== undefined) clearInterval(interval);
            return 100;
          }
          return Math.min(prevProgress + randomInc, 95);
        });
      }, 200);
    }

    return () => {
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };
  }, [pointcloudLoading, setLoadingProgression]);

  useEffect(() => {
    if(loadingProgression === 100){
      const hideLoaderTimeout = setTimeout(() => {
        setPointcloudLoading(false);
      }, 1000);
  
      return () => clearTimeout(hideLoaderTimeout);
    }
  },  [loadingProgression, setPointcloudLoading]);

  return (
    <Box
      sx={{ height: "0", position: "relative" }}
      ref={parentRef}
    >
      {(anyoneLoading) && (
        <Stack
          alignItems="center"
          sx={{
            backgroundColor: "black",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            display:"none",
          }}
        >
        </Stack>
      )}
      <Box
        component="div"
        ref={potreeContainerDiv}
        className="potree_container"
        sx={{ opacity: anyoneLoading ? 0 : 1 }}
      >
        <Box component="div" id="potree_render_area"></Box>
        <Box
          component="div"
          id="potree_sidebar_container"
          sx={{
            transition: "transform 0.3s ease",
            transform: menu ? "translateX(0)" : "translateX(-5%)",
            display:  "none",
            width: {
              xs: "150px !important",
              sm: "340px !important",
            },
          }}
        ></Box>
        {isSplit && (
          <Stack
            sx={{
              position: "absolute",
              height: "100%",
              left: width,
              zIndex: 1000,
            }}
          >
            <Stack
              sx={{
                position: "absolute",
                height: "100%",
                left: "50%",
                width: "0.1rem",
                backgroundColor: "background.default",
              }}
            >
              <Divider orientation="vertical" flexItem />
            </Stack>
            <Stack
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translate(-50%, -50%)",
                display: "none",
              }}
            >
              <IconButton
                sx={{ color: "grey.500" }}
              >
                <SwapHorizontalCircle fontSize="large" />
              </IconButton>
            </Stack>
          </Stack>

        )}
      </Box>
      {allLoading && (
        <>
          <IconButton
            sx={(theme) => ({
              position: "absolute",
              top: {
                xs: 5,
              },
              left: {
                xs: menu ? "280px" : "5px",
                sm: menu ? "350px" : "5px",
              },
              display: "none",
              transition: "left 0.5s ease",
              zIndex: 1000,
              color: theme.palette.grey[500],
            })}
            onClick={() => {
              setMenuOpen(!menu);
            }}
          >
            {menu ? <MenuOpen fontSize="large" /> : <Menu fontSize="large" />}
          </IconButton>
          <IconButton
            sx={(theme) => ({
              position: "absolute",
              top: {
                xs: -45,
              },
              left: {
                xs: menu ? "280px" : "5px",
                sm: menu ? "350px" : "5px",
              },
              display: "none",
              transition: "left 0.5s ease",
              zIndex: 1000,
              color: theme.palette.grey[500],
            })}
            onClick={() => {
              viewerRef.current?.toggleMap();
              // setMapToggle(!mapToggle);
            }}
          >
            <ExploreOutlined color="primary" fontSize="large" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default PointCloudView;
